import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, KeyRound, ArrowLeft, RefreshCw } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { dashPathFor } from "./GoogleAuth";

export const OtpLogin = ({ register = false }) => {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [purpose, setPurpose] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const send = async (e) => {
    e?.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/auth/send-otp", { email });
      setDevCode(data.dev_code || ""); setPurpose(data.purpose); setStep("code");
      toast.success(data.sent ? `Code sent to ${email}` : "Test mode: use the code shown");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const verify = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email, code, name });
      login(data.token, data.user);
      toast.success(data.new_user ? "Email verified — let's set up your profile" : `Welcome back, ${data.user.name}!`);
      navigate(data.new_user ? "/onboarding" : dashPathFor(data.user), { replace: true });
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  if (step === "email") return (
    <form onSubmit={send} className="mt-7 space-y-3" data-testid="otp-email-step">
      <label className="nb-label">Email address</label>
      <div className="relative"><Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="otp-email-input" type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="nb-input" style={{ paddingLeft: 38 }} /></div>
      <button data-testid="otp-send-btn" disabled={busy} className="nb-btn nb-btn-lime w-full justify-center">{busy ? "Sending…" : register ? "Send sign-up code" : "Send login code"}</button>
    </form>
  );
  return (
    <form onSubmit={verify} className="mt-7 space-y-3" data-testid="otp-code-step">
      <p className="text-sm text-slate-600">We sent a 6-digit code to <b data-testid="otp-sent-to">{email}</b>. {purpose === "signup" ? "This creates a new account." : "Enter it to sign in."}</p>
      {devCode && <p data-testid="otp-dev-code" className="text-xs font-bold rounded-lg px-3 py-2" style={{ background: "#FEF3C7" }}>Test mode (email not configured): your code is <span className="font-display tracking-widest">{devCode}</span></p>}
      {purpose === "signup" && <input data-testid="otp-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="nb-input" />}
      <div className="relative"><KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="otp-code-input" inputMode="numeric" pattern="[0-9]*" maxLength={6} required autoFocus value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="6-digit code" className="nb-input font-display tracking-[0.4em] text-lg" style={{ paddingLeft: 38 }} /></div>
      <button data-testid="otp-verify-btn" disabled={busy || code.length !== 6} className="nb-btn nb-btn-lime w-full justify-center">{busy ? "Verifying…" : "Verify & continue"}</button>
      <div className="flex justify-between text-xs font-bold">
        <button type="button" data-testid="otp-change-email" onClick={() => { setStep("email"); setCode(""); }} className="text-slate-500 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Change email</button>
        <button type="button" data-testid="otp-resend" onClick={send} disabled={busy} className="text-emerald-700 flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Resend code</button>
      </div>
    </form>
  );
};
